import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { PageHeader, EmptyState, Section } from "@/components/Shared";
import api, { formatApiError } from "@/lib/api";
import { TL, formatDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, BedDouble, Wallet, TrendingUp, Percent, Download } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";

const CHANNELS = ["Booking.com", "Airbnb", "Direkt", "Acente", "Trivago", "Expedia", "Diğer"];
const CHANNEL_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(150 30% 40%)",
  "hsl(30 60% 50%)",
];

function nightsBetween(ci, co) {
  if (!ci || !co) return 0;
  const a = new Date(ci); const b = new Date(co);
  const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
  return Number.isFinite(diff) && diff >= 0 ? diff : 0;
}

function emptyForm() {
  return {
    guest_name: "",
    check_in: todayISO(),
    check_out: todayISO(),
    channel: "Booking.com",
    payment_amount: "",
    commission_amount: "",
    deposit_amount: "",
    account_id: "",
    payment_method: "havale",
    payment_cari_id: "",
    deposit_account_id: "",
    deposit_method: "havale",
    deposit_cari_id: "",
    notes: "",
  };
}

function nextBusinessDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() === 0 || d.getDay() === 6);
  return d.toISOString().slice(0, 10);
}

function ReservationForm({ open, onOpenChange, editing, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [customChannel, setCustomChannel] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [cariler, setCariler] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/hesaplar").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
      api.get("/cariler").then((r) => setCariler(r.data)).catch(() => setCariler([]));
    }
  }, [open]);

  useEffect(() => {
    if (editing) {
      setForm({
        guest_name: editing.guest_name || "",
        check_in: editing.check_in || todayISO(),
        check_out: editing.check_out || todayISO(),
        channel: editing.channel || "Direkt",
        payment_amount: editing.payment_amount ?? "",
        commission_amount: editing.commission_amount ?? "",
        deposit_amount: editing.deposit_amount ?? "",
        account_id: editing.account_id || "",
        payment_method: editing.payment_method || "havale",
        payment_cari_id: editing.payment_cari_id || "",
        deposit_account_id: editing.deposit_account_id || "",
        deposit_method: editing.deposit_method || "havale",
        deposit_cari_id: editing.deposit_cari_id || "",
        notes: editing.notes || "",
      });
      setCustomChannel(!CHANNELS.includes(editing.channel));
    } else {
      setForm(emptyForm());
      setCustomChannel(false);
    }
  }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payIsCari = form.payment_method === "cari_odeme";
      const depIsCari = form.deposit_method === "cari_odeme";
      const payload = {
        guest_name: form.guest_name.trim(),
        check_in: form.check_in,
        check_out: form.check_out,
        channel: (form.channel || "").trim() || "Diğer",
        payment_amount: parseFloat(form.payment_amount) || 0,
        commission_amount: parseFloat(form.commission_amount) || 0,
        deposit_amount: parseFloat(form.deposit_amount) || 0,
        account_id: !payIsCari && form.account_id && form.account_id !== "__none__" ? form.account_id : null,
        payment_method: form.payment_method || "havale",
        payment_cari_id: payIsCari && form.payment_cari_id && form.payment_cari_id !== "__none__" ? form.payment_cari_id : null,
        deposit_account_id: !depIsCari && form.deposit_account_id && form.deposit_account_id !== "__none__" ? form.deposit_account_id : null,
        deposit_method: form.deposit_method || "havale",
        deposit_cari_id: depIsCari && form.deposit_cari_id && form.deposit_cari_id !== "__none__" ? form.deposit_cari_id : null,
        notes: form.notes,
      };
      if (editing) {
        await api.patch(`/rezervasyonlar/${editing.id}`, payload);
        toast.success("Rezervasyon güncellendi");
      } else {
        await api.post("/rezervasyonlar", payload);
        toast.success("Rezervasyon eklendi");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const nights = nightsBetween(form.check_in, form.check_out);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Rezervasyon Düzenle" : "Yeni Rezervasyon"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Misafir Ad Soyad</Label>
            <Input required value={form.guest_name}
              onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
              data-testid="reservation-guest-name" placeholder="Örn. Ali Yılmaz" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Giriş Tarihi</Label>
              <Input required type="date" value={form.check_in}
                onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                data-testid="reservation-check-in" />
            </div>
            <div className="space-y-2">
              <Label>Çıkış Tarihi</Label>
              <Input required type="date" value={form.check_out}
                onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                data-testid="reservation-check-out" />
            </div>
          </div>
          {nights > 0 && (
            <div className="text-xs text-foreground/60" data-testid="reservation-nights">Konaklama: {nights} gece</div>
          )}
          <div className="space-y-2">
            <Label>Rezervasyon Kanalı</Label>
            {customChannel ? (
              <div className="flex gap-2">
                <Input value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  data-testid="reservation-channel-custom" placeholder="Kanal adı" />
                <Button type="button" variant="outline" onClick={() => { setCustomChannel(false); setForm({ ...form, channel: "Booking.com" }); }}>
                  Listeden seç
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Select value={form.channel} onValueChange={(v) => {
                  if (v === "__custom__") { setCustomChannel(true); setForm({ ...form, channel: "" }); }
                  else setForm({ ...form, channel: v });
                }}>
                  <SelectTrigger data-testid="reservation-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__custom__">+ Yeni kanal ekle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Ödeme (₺)</Label>
              <Input type="number" step="0.01" min="0" value={form.payment_amount}
                onChange={(e) => setForm({ ...form, payment_amount: e.target.value })}
                data-testid="reservation-payment" placeholder="0.00" />
            </div>
            <div className="space-y-2">
              <Label>Komisyon (₺)</Label>
              <Input type="number" step="0.01" min="0" value={form.commission_amount}
                onChange={(e) => setForm({ ...form, commission_amount: e.target.value })}
                data-testid="reservation-commission" placeholder="0.00" />
            </div>
            <div className="space-y-2">
              <Label>Kapora (₺)</Label>
              <Input type="number" step="0.01" min="0" value={form.deposit_amount}
                onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })}
                data-testid="reservation-deposit" placeholder="0.00" />
            </div>
          </div>
          <div className="border border-border rounded-md p-4 space-y-3 bg-secondary/20">
            <div className="text-xs uppercase tracking-wider text-foreground/60">Ödeme (Toplam Tutar)</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Yöntem</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="reservation-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="havale">Havale / EFT</SelectItem>
                    <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                    <SelectItem value="enpara">Enpara</SelectItem>
                    <SelectItem value="nakit">Nakit</SelectItem>
                    <SelectItem value="cari_odeme">Cari Ödeme (Cari Hesaba Yaz)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.payment_method === "cari_odeme" ? (
                <div className="space-y-2">
                  <Label>Cari Hesap</Label>
                  <Select
                    key={`pay-cari-${form.payment_method}`}
                    value={form.payment_cari_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, payment_cari_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="reservation-payment-cari-select"><SelectValue placeholder="Cari seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Cari seçme</SelectItem>
                      {cariler.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Hedef Hesap (opsiyonel)</Label>
                  <Select
                    key={`pay-acc-${form.payment_method}`}
                    value={form.account_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, account_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="reservation-account-select"><SelectValue placeholder="Hesap seçme" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Hesap seçme</SelectItem>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name} ({a.type === "kasa" ? "Kasa" : "Banka"})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {form.payment_method === "cari_odeme" && parseFloat(form.payment_amount || 0) > 0 && (
              <div className="text-xs text-primary bg-primary/5 border border-primary/20 rounded px-3 py-2" data-testid="cari-odeme-notice">
                Ödeme banka/kasa bakiyesine yansımayacak. Seçilen cariye <b>Alacak</b> olarak yazılacak. Cari daha sonra ödediğinde <b>Tahsilat</b> olarak kaydedin — otomatik olarak cari bakiyesinden düşülür.
              </div>
            )}
            {form.payment_method === "kredi_karti" && parseFloat(form.payment_amount || 0) > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="kredi-karti-notice">
                Ödeme <b>Blokede Bakiye</b> olarak kaydedilecek. Tahmini valör tarihi:{" "}
                <b data-testid="valor-hint-payment">{formatDate(nextBusinessDay(form.check_in))}</b> (ertesi iş günü).
              </div>
            )}
          </div>
          <div className="border border-border rounded-md p-4 space-y-3 bg-secondary/20">
            <div className="text-xs uppercase tracking-wider text-foreground/60">Kapora (Ön Ödeme)</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Yöntem</Label>
                <Select value={form.deposit_method} onValueChange={(v) => setForm({ ...form, deposit_method: v })}>
                  <SelectTrigger data-testid="reservation-deposit-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="havale">Havale / EFT</SelectItem>
                    <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                    <SelectItem value="enpara">Enpara</SelectItem>
                    <SelectItem value="nakit">Nakit</SelectItem>
                    <SelectItem value="cari_odeme">Cari Ödeme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.deposit_method === "cari_odeme" ? (
                <div className="space-y-2">
                  <Label>Cari Hesap</Label>
                  <Select
                    key={`dep-cari-${form.deposit_method}`}
                    value={form.deposit_cari_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, deposit_cari_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="reservation-deposit-cari-select"><SelectValue placeholder="Cari seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Cari seçme</SelectItem>
                      {cariler.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Hedef Hesap (opsiyonel)</Label>
                  <Select
                    key={`dep-acc-${form.deposit_method}`}
                    value={form.deposit_account_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, deposit_account_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="reservation-deposit-account-select"><SelectValue placeholder="Hesap seçme" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Hesap seçme</SelectItem>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name} ({a.type === "kasa" ? "Kasa" : "Banka"})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {form.deposit_method === "kredi_karti" && parseFloat(form.deposit_amount || 0) > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="kredi-karti-notice-deposit">
                Kapora <b>Blokede Bakiye</b> olarak kaydedilecek. Tahmini valör tarihi:{" "}
                <b data-testid="valor-hint-deposit">{formatDate(nextBusinessDay(form.check_in))}</b> (ertesi iş günü).
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Notlar</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="reservation-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-reservation">Vazgeç</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="submit-reservation">
              {saving ? "Kaydediliyor..." : (editing ? "Güncelle" : "Kaydet")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, icon: Icon, tone = "default", testid }) {
  const toneClass = {
    default: "text-foreground",
    success: "text-[hsl(var(--success))]",
    destructive: "text-destructive",
    primary: "text-primary",
  }[tone];
  return (
    <div className="border border-border rounded-md bg-background p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider text-foreground/50">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-foreground/40" strokeWidth={1.5} />}
      </div>
      <div className={`font-heading text-3xl font-medium tracking-tight mt-3 tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function RezervasyonlarPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [byChannel, setByChannel] = useState([]);
  const [byMethod, setByMethod] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pending, setPending] = useState(null);
  const [filters, setFilters] = useState({ start: "", end: "", channel: "all" });

  const load = async () => {
    const params = {};
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    if (filters.channel && filters.channel !== "all") params.channel = filters.channel;
    const [list, s, m, ch, pm] = await Promise.all([
      api.get("/rezervasyonlar", { params }),
      api.get("/rezervasyon-rapor/ozet"),
      api.get("/rezervasyon-rapor/aylik?months=6"),
      api.get("/rezervasyon-rapor/kanal"),
      api.get("/rezervasyon-rapor/odeme-yontemi"),
    ]);
    setItems(list.data);
    setSummary(s.data);
    setMonthly(m.data);
    setByChannel(ch.data);
    setByMethod(pm.data);
  };
  useEffect(() => { load().catch(console.error); }, []); // eslint-disable-line

  const doDelete = async () => {
    if (!pending) return;
    try {
      await api.delete(`/rezervasyonlar/${pending.id}`);
      toast.success(`"${pending.guest_name}" silindi`);
      setPending(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setDialogOpen(true); };

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start) params.set("start", filters.start);
      if (filters.end) params.set("end", filters.end);
      if (filters.channel && filters.channel !== "all") params.set("channel", filters.channel);
      const res = await api.get(`/rezervasyonlar/export/pdf?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `rezervasyonlar-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const channelOptions = useMemo(() => {
    const seen = new Set(CHANNELS);
    items.forEach((r) => r.channel && seen.add(r.channel));
    return Array.from(seen);
  }, [items]);

  const hasMonthly = monthly.some((m) => m.odeme || m.komisyon);

  return (
    <Layout>
      <PageHeader
        title="Rezervasyonlar"
        subtitle="Günlük rezervasyonlarınızı, ödemeleri ve komisyonları takip edin"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-button">
              <Download className="w-4 h-4 mr-2" /> PDF İndir
            </Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={openNew} data-testid="add-reservation-button">
              <Plus className="w-4 h-4 mr-2" /> Yeni Rezervasyon
            </Button>
          </div>
        }
        testId="rezervasyonlar-header"
      />

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="reservation-metrics">
          <Metric label="Toplam Rezervasyon" value={summary.count} icon={BedDouble} testid="metric-count" />
          <Metric label="Toplam Ödeme" value={TL(summary.total_odeme)} icon={Wallet} tone="primary" testid="metric-total-payment" />
          <Metric label="Toplam Komisyon" value={TL(summary.total_komisyon)} icon={Percent} tone="destructive" testid="metric-total-commission" />
          <Metric label="Net Gelir" value={TL(summary.net_gelir)} icon={TrendingUp} tone="success" testid="metric-net-income" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2">
          <Section title="Aylık Ödeme & Komisyon (Son 6 Ay)">
            <div className="border border-border rounded-md p-6 bg-background">
              {!hasMonthly ? (
                <EmptyState title="Grafik için henüz veri yok" hint="Rezervasyon eklediğinizde grafik oluşacak." testId="empty-monthly-chart" />
              ) : (
                <div className="h-72" data-testid="monthly-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} formatter={(v) => TL(v)} />
                      <Legend />
                      <Bar dataKey="odeme" fill="hsl(var(--chart-1))" name="Ödeme" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="komisyon" fill="hsl(var(--chart-2))" name="Komisyon" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Section>
        </div>
        <div>
          <Section title="Kanal Dağılımı (Ödeme)">
            <div className="border border-border rounded-md p-6 bg-background">
              {byChannel.length === 0 ? (
                <EmptyState title="Veri yok" testId="empty-channel-chart" />
              ) : (
                <div className="h-72" data-testid="channel-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={byChannel} dataKey="odeme" nameKey="channel" outerRadius={90} label>
                        {byChannel.map((entry, idx) => <Cell key={entry.channel} fill={CHANNEL_COLORS[idx % CHANNEL_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} formatter={(v) => TL(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Section>
        </div>
      </div>

      <Section title="Ödeme Yöntemi Kırılımı">
        <div className="border border-border rounded-md p-6 bg-background">
          {byMethod.every((m) => m.total === 0) ? (
            <EmptyState title="Henüz ödeme kaydı yok" testId="empty-method-chart" />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center" data-testid="method-chart">
              <div className="lg:col-span-2 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byMethod.filter((m) => m.total > 0)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} formatter={(v) => TL(v)} />
                    <Legend />
                    <Bar dataKey="odeme" fill="hsl(var(--chart-1))" name="Ödeme" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="kapora" fill="hsl(var(--chart-4))" name="Kapora" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {byMethod.map((m) => (
                  <div key={m.method} className="flex items-center justify-between px-3 py-2 border border-border rounded-md bg-secondary/20" data-testid={`method-row-${m.method}`}>
                    <div>
                      <div className="text-sm font-medium text-foreground">{m.label}</div>
                      <div className="text-[11px] text-foreground/50">{m.count} ödeme</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium tabular-nums">{TL(m.total)}</div>
                      {m.kapora > 0 && <div className="text-[11px] text-foreground/60 tabular-nums">Kapora: {TL(m.kapora)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>

      <div className="border border-border rounded-md bg-background p-4 mb-4" data-testid="reservation-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-foreground/60">Giriş tarihi ≥</Label>
            <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="filter-start" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-foreground/60">Giriş tarihi ≤</Label>
            <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="filter-end" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-foreground/60">Kanal</Label>
            <Select value={filters.channel} onValueChange={(v) => setFilters({ ...filters, channel: v })}>
              <SelectTrigger data-testid="filter-channel"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {channelOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="apply-filters">
            Uygula
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-md bg-background overflow-hidden" data-testid="reservations-table">
        {items.length === 0 ? (
          <EmptyState title="Rezervasyon bulunmuyor" hint="Yeni rezervasyon ekleyerek başlayın." testId="reservations-empty" />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-foreground/70">
              <tr>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Misafir</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Giriş</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Çıkış</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Kanal</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Ödeme</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Komisyon</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Kapora</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Net</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider w-24">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`reservation-row-${r.id}`}>
                  <td className="py-3 px-4 font-medium text-foreground">{r.guest_name}</td>
                  <td className="py-3 px-4 text-foreground/80">{formatDate(r.check_in)}</td>
                  <td className="py-3 px-4 text-foreground/80">{formatDate(r.check_out)}</td>
                  <td className="py-3 px-4">
                    <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-sm text-xs border border-border">{r.channel}</span>
                  </td>
                  <td className="py-3 px-4 text-right tabular-nums">{TL(r.payment_amount)}</td>
                  <td className="py-3 px-4 text-right tabular-nums text-destructive">{TL(r.commission_amount)}</td>
                  <td className="py-3 px-4 text-right tabular-nums text-foreground/70">{TL(r.deposit_amount)}</td>
                  <td className="py-3 px-4 text-right tabular-nums font-medium text-[hsl(var(--success))]">{TL(r.net_amount)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openEdit(r)} className="text-foreground/40 hover:text-primary transition-colors p-1"
                        data-testid={`edit-reservation-${r.id}`} aria-label="Düzenle">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => setPending(r)} className="text-foreground/40 hover:text-destructive transition-colors p-1"
                        data-testid={`delete-reservation-${r.id}`} aria-label="Sil">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ReservationForm open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={load} />

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="delete-reservation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Rezervasyon silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{pending?.guest_name}</span> rezervasyonu kalıcı olarak silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-reservation">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-reservation">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
